// Centralized reCAPTCHA Enterprise helper (web)
// Usage: import { fetchRecaptchaToken } from 'src/util/recaptcha';
// const token = await fetchRecaptchaToken('login');
// NOTE: This implementation enforces Enterprise-only execution. It expects you have
// created an Enterprise site key in Google Cloud and exposed it at runtime (never the secret/service account credentials).
// Environment / runtime injection options (one of):
//   window._env_.RECAPTCHA_SITE_KEY
//   process.env.REACT_APP_RECAPTCHA_SITE_KEY (build-time)
//   window.RECAPTCHA_SITE_KEY
// The backend must verify via the reCAPTCHA Enterprise Assessments API (NOT siteverify) now.

const DEFAULT_TIMEOUT_MS = 6000; // extended to reduce false negatives due to slow script/init
const DEFAULT_ACTION = 'phone_auth';
type RecaptchaExecError = 'enterprise_execute_unavailable' | 'timeout' | 'unknown';

function getSiteKey(): string | undefined {
  const key =
    (window as any)._env_?.RECAPTCHA_SITE_KEY ||
    (window as any).RECAPTCHA_SITE_KEY ||
    process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  if (!key) {
    console.error('[reCAPTCHA] Site key missing – aborting token request');
  }
  return key;
}

function loadScript(siteKey: string, forceReload = false): Promise<void> {
  return new Promise((resolve, reject) => {
    if (!siteKey) return reject(new Error('Missing site key'));
    if (!forceReload && (window as any).grecaptcha) {
      const g: any = (window as any).grecaptcha;
      if (typeof g.ready === 'function') {
        console.log('[reCAPTCHA] Script already present & ready');
        return resolve();
      } else {
        console.warn('[reCAPTCHA] grecaptcha object present WITHOUT ready(); forcing reload');
        forceReload = true;
      }
    }
    const existing = document.getElementById('recaptcha-script');
    if (existing && !forceReload) {
      console.log('[reCAPTCHA] Existing script tag found, waiting for load');
      existing.addEventListener('load', () => resolve());
      setTimeout(() => resolve(), 2500); // best effort
      return;
    }
    if (existing && forceReload) {
      existing.parentElement?.removeChild(existing);
    }
    const s = document.createElement('script');
    s.id = 'recaptcha-script';
    s.async = true;
    s.defer = true;
    // Enterprise specific loader per docs: https://cloud.google.com/recaptcha/docs/instrument-web-pages
    s.src = `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}&ts=${Date.now()}`; // cache-bust when reloading
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Failed to load reCAPTCHA script'));
    document.head.appendChild(s);
  });
}

// Simpler approach per official snippet: rely on grecaptcha.enterprise.ready callback.
// We only do a forced reload once if enterprise.execute is missing when the ready callback fires.

export async function fetchRecaptchaToken(action: string = DEFAULT_ACTION, timeoutMs: number = DEFAULT_TIMEOUT_MS): Promise<string | undefined> {
  try {
    const siteKey = getSiteKey();
    if (!siteKey) return undefined;
    console.log(`[reCAPTCHA] Fetch token start action="${action}"`);
    await loadScript(siteKey);
    const grecaptcha: any = (window as any).grecaptcha;
    if (!grecaptcha || typeof grecaptcha.enterprise === 'undefined') {
      console.warn('[reCAPTCHA] grecaptcha or grecaptcha.enterprise not initialized immediately after script load');
    }

    const tokenPromise = new Promise<string>((resolve, reject) => {
      const runExecute = async () => {
        try {
          const g: any = (window as any).grecaptcha;
          if (!g || !g.enterprise || typeof g.enterprise.execute !== 'function') {
            console.warn('[reCAPTCHA] Enterprise API not ready inside ready() callback, forcing single reload');
            await loadScript(siteKey, true);
            const g2: any = (window as any).grecaptcha;
            if (!g2 || !g2.enterprise || typeof g2.enterprise.execute !== 'function') {
              console.error('[reCAPTCHA] Enterprise API still unavailable after reload');
              return reject(new Error('enterprise_execute_unavailable'));
            }
            console.log('[reCAPTCHA] Enterprise API became available after reload');
          }
          console.log(`[reCAPTCHA] Executing enterprise.execute for action="${action}"`);
          const token = await (window as any).grecaptcha.enterprise.execute(siteKey, { action });
          console.log(`[reCAPTCHA] Token generated action="${action}" length=${token?.length}`);
          resolve(token);
        } catch (err) {
          console.warn('[reCAPTCHA] Token generation error', err);
          reject(err);
        }
      };
      try {
        (window as any).grecaptcha.enterprise.ready(runExecute);
      } catch (e) {
        console.warn('[reCAPTCHA] Calling enterprise.ready failed (possibly undefined). Retrying after reload once.', e);
        loadScript(siteKey, true).then(() => {
          try {
            (window as any).grecaptcha.enterprise.ready(runExecute);
          } catch (e2) {
            reject(e2);
          }
        }).catch(reject);
      }
    });

    const timeoutPromise = new Promise<undefined>((resolve) => {
      setTimeout(() => resolve(undefined), timeoutMs);
    });

    const result = (await Promise.race([tokenPromise, timeoutPromise])) as string | undefined;
    if (!result) {
      console.warn(`[reCAPTCHA] Token acquisition timed out after ${timeoutMs}ms for action="${action}"`);
    }
    return result;
  } catch (err) {
    console.warn('fetchRecaptchaToken failed', err);
    return undefined;
  }
}
